'use client';

import React from 'react';
import { OptionItem } from '../OptionItem';
import { InteractiveOptionProps, ReviewOptionProps } from '../interfaces';

/**
 * Single Responsibility: Handle single choice question options
 * Open/Closed: Can be extended without modifying base OptionItem
 */

interface SingleChoiceOptionsBaseProps {
  question: {
    single_choice_question?: {
      answers: string[];
    };
    metadata_uid: string;
  };
  questionIndex: number;
  selectedOptions: string | string[] | boolean | undefined;
  isQuiz?: boolean;
  correctAnswers?: string | string[] | boolean | null;
  quizData?: any;
  isQuestionSubmitted?: boolean;
}

export function SingleChoiceOptionsInteractive({
  question,
  questionIndex,
  selectedOptions,
  handlers,
  isQuiz = false,
  correctAnswers = null,
  isQuestionSubmitted = false,
}: SingleChoiceOptionsBaseProps & { handlers: any }) {
  const isChecked = (option: string) => selectedOptions === option;

  const getInputClassName = (option: string) => {
    if (!isQuiz || !isQuestionSubmitted) {
      return 'form-check-input outline-focussed cursor-pointer w-5 h-5';
    }

    const isSelected = isChecked(option);

    if (correctAnswers) {
      // User got it wrong - highlight all options
      const isCorrectOption = correctAnswers === option;
      return isCorrectOption
        ? 'form-check-input outline-focussed cursor-pointer w-5 h-5 right-answer'
        : 'form-check-input outline-focussed cursor-pointer w-5 h-5 wrong-answer';
    }

    // User got it right - show selected option as correct
    if (isSelected) {
      return 'form-check-input outline-focussed cursor-pointer w-5 h-5 right-answer';
    }
    return 'form-check-input outline-focussed cursor-pointer w-5 h-5';
  };

  const getFeedbackText = (option: string) => {
    if (!isQuiz || !isQuestionSubmitted) return null;

    const isSelected = isChecked(option);

    if (correctAnswers) {
      // User got it wrong - show Correct/Incorrect for all options
      const isCorrectOption = correctAnswers === option;
      return isCorrectOption
        ? { text: 'Correct', className: 'text-green' }
        : { text: 'Incorrect', className: 'text-red' };
    }

    // User got it right - show Correct only for selected option
    if (isSelected) {
      return { text: 'Correct', className: 'text-green' };
    }
    return null;
  };

  return (
    <>
      {question.single_choice_question?.answers.map((option: string, qi: number) => {
        const feedback = getFeedbackText(option);
        const shouldShowFeedback = isQuestionSubmitted && feedback != null;

        return (
          <div
            key={qi + question.metadata_uid}
            className="form-check flex items-center"
            role="group"
          >
            <span className="relative">
              <input
                type="radio"
                className={getInputClassName(option)}
                name={question.metadata_uid}
                id={`${option}-${questionIndex}`}
                value={option}
                required
                checked={isChecked(option)}
                onChange={(e) => handlers.handleSingleChoice(e.target.value)}
                disabled={isQuestionSubmitted}
              />
            </span>
            <label
              className={`cs-s-body-regular text-text-default-strong select-none m-0 ${isQuestionSubmitted ? 'cursor-default' : 'cursor-pointer'}`}
              htmlFor={`${option}-${questionIndex}`}
            >
              {option}
              {shouldShowFeedback && feedback && (
                <>
                  <br />
                  <span className={`cs-caption block ${feedback.className}`}>
                    {feedback.text}
                  </span>
                </>
              )}
            </label>
          </div>
        );
      })}
      {isQuestionSubmitted && correctAnswers && (
        <div className="correct-answer position-relative">
          <p>The correct answer was:</p>
          <p className="!cs-s-body-regular lh-150">{typeof correctAnswers === 'string' ? correctAnswers : ''}</p>
        </div>
      )}
    </>
  );
}

export function SingleChoiceOptionsReview({
  question,
  questionIndex,
  selectedOptions,
  showCorrectness,
  correctnessChecker,
  correctAnswers = null,
}: SingleChoiceOptionsBaseProps & {
  showCorrectness: boolean;
  correctnessChecker?: (option: string | boolean) => boolean | null;
}) {
  const isChecked = (option: string) => selectedOptions === option;

  return (
    <>
      {question.single_choice_question?.answers.map((option: string, qi: number) => {
        const isSelected = isChecked(option);
        const correctness = correctnessChecker ? correctnessChecker(option) : null;

        return (
          <OptionItem
            key={qi + question.metadata_uid}
            option={option}
            isSelected={isSelected}
            questionUid={question.metadata_uid}
            questionIndex={questionIndex}
            optionIndex={qi}
            label={option}
            inputType="radio"
            isDisabled={true}
            correctness={correctness}
            showCorrectness={showCorrectness}
          />
        );
      })}
      {correctAnswers != null && (
        <div className="correct-answer position-relative">
          <p>The correct answer was:</p>
          <p className="!cs-s-body-regular lh-150">
            {typeof correctAnswers === 'string' ? correctAnswers : ''}
          </p>
        </div>
      )}
    </>
  );
}

